import React, { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { EmailSignalEngine } from '../../utils/emailSignalData';
import { Button } from '../ui/button';

function tierColor(tier) {
  switch (tier) {
    case 'critical': return '#EF4444';
    case 'high': return '#F59E0B';
    case 'moderate': return '#2C5FA8';
    default: return '#10B981';
  }
}

export default function InterventionQueue({ onResolve, onDismiss }) {
  const [queue, setQueue] = useState([]);
  const [draftFor, setDraftFor] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = () => {
    setQueue(EmailSignalEngine.getInterventionQueue());
  };

  useEffect(() => {
    load();
  }, []);

  const handleResolve = (threadId) => {
    EmailSignalEngine.resolveIntervention(threadId);
    load();
    onResolve?.();
  };

  const handleDismiss = (threadId) => {
    EmailSignalEngine.dismissIntervention(threadId);
    load();
    onDismiss?.();
  };

  const openDraft = (thread) => {
    setDraftFor(thread.id);
    setDraft(EmailSignalEngine.getDraftMessage(thread.id));
  };

  const copyDraft = () => {
    if (!draft) return;
    const text = `Subject: ${draft.subject}\n\n${draft.body}`;
    navigator.clipboard.writeText(text).then(() => {
      if (typeof window !== 'undefined') {
        const msg = document.createElement('div');
        msg.textContent = 'Copied to clipboard';
        msg.style.cssText = 'position:fixed;bottom:20px;right:20px;background:#10B981;color:white;padding:12px 20px;border-radius:8px;font-size:14px;z-index:9999;';
        document.body.appendChild(msg);
        setTimeout(() => msg.remove(), 2000);
      }
    });
  };

  if (queue.length === 0) {
    return (
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ marginBottom: 16 }}><CheckCircle size={48} strokeWidth={2} color="#2EB87A" /></div>
          <h3 className="text-lg font-semibold" style={{ marginBottom: 8 }}>No interventions needed</h3>
          <p className="text-text-muted">No threads currently above the high-risk threshold. Check back after new signals arrive.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Intervention queue</div>
          <div className="text-sm text-gray-500">Sorted by risk score (highest first)</div>
        </div>
        <div className="p-5 space-y-4">
          {queue.map(thread => (
            <div
              key={thread.id}
              style={{
                padding: 20,
                background: '#F9FAFB',
                borderRadius: 8,
                borderLeft: `4px solid ${tierColor(thread.riskTier)}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 700,
                        background: `${tierColor(thread.riskTier)}20`,
                        color: tierColor(thread.riskTier),
                      }}
                    >
                      {thread.riskScore} · {thread.riskTier}
                    </span>
                    <span className="text-body text-text-muted">{thread.category}</span>
                  </div>
                  <h4 className="text-base font-semibold" style={{ marginBottom: 6 }}>{thread.subjectMasked}</h4>
                  <p className="text-body text-text-muted" style={{ marginBottom: 8 }}>{thread.explanation}</p>
                  <div className="text-body-sm" style={{ color: '#9CA3AF' }}>
                    Recommended: {thread.recommendedAction} · Route to: <strong>{thread.suggestedOwner}</strong>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                <Button variant="default" size="sm" type="button" onClick={() => openDraft(thread)}>
                  Draft message
                </Button>
                <Button variant="primary" size="sm" type="button" onClick={() => handleResolve(thread.id)}>
                  Resolved
                </Button>
                <Button variant="default" size="sm" type="button" onClick={() => handleDismiss(thread.id)}>
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Draft message modal */}
      {draftFor && draft && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setDraftFor(null)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              maxWidth: 600,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              padding: 24,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="text-lg font-semibold">Draft escalation message</h3>
              <Button type="button" variant="ghost" size="icon" onClick={() => setDraftFor(null)} className="text-text-muted">×</Button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="text-body-sm font-semibold" style={{ display: 'block', marginBottom: 4 }}>Subject</label>
              <input
                type="text"
                value={draft.subject}
                readOnly
                style={{ width: '100%', padding: 8, border: '1px solid #D1D5DB', borderRadius: 6 }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="text-body-sm font-semibold" style={{ display: 'block', marginBottom: 4 }}>Body</label>
              <textarea
                value={draft.body}
                onChange={e => setDraft({ ...draft, body: e.target.value })}
                rows={10}
                style={{ width: '100%', padding: 8, border: '1px solid #D1D5DB', borderRadius: 6, fontFamily: 'inherit' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="primary" size="sm" type="button" onClick={copyDraft}>
                Copy to clipboard
              </Button>
              <Button variant="default" size="sm" type="button" onClick={() => setDraftFor(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
